'use client'

/**
 * Purpose: Product-card modal explaining a compatibility score trait by trait.
 * Inputs: `productName`, 0–4 `score`, per-trait `matches`, and `onClose`.
 * Outputs: A portalled `role="dialog"` with focus trapped inside; Escape,
 * backdrop click, or the ✕ button call `onClose`, restoring focus to the trigger.
 */

import { createPortal } from 'react-dom'
import { useTranslations } from 'next-intl'
import { FaceImage } from '@components/images/FaceImage/FaceImage'
import { useFocusTrap } from '@/hooks/use-focus-trap'
import type { CompatibilityMatch } from '@lib/products'
import './compatibilityModal.css'

type Props = {
  productName: string
  score: number
  matches: CompatibilityMatch['matches']
  onClose: () => void
}

function badgeColor(score: number): string {
  switch (score) {
    case 0: return '#b3261e'
    case 1: return '#c2622a'
    case 2: return '#b58900'
    case 3: return 'color-mix(in srgb, var(--color-green) 85%, white)'
    default: return 'var(--color-green)'
  }
}

export function CompatibilityModal({ productName, score, matches, onClose }: Props) {
  const t = useTranslations('productCard')
  // Traps Tab inside the content box, closes on Escape, and restores focus on unmount.
  const contentRef = useFocusTrap<HTMLDivElement>(onClose)
  const percent = score * 25
  const traits = [
    { key: 'moisture', label: t('compatibilityTrait.moisture'), isMatch: matches.moisture },
    { key: 'sensitivity', label: t('compatibilityTrait.sensitivity'), isMatch: matches.sensitivity },
    { key: 'pigmentation', label: t('compatibilityTrait.pigmentation'), isMatch: matches.pigmentation },
    { key: 'elasticity', label: t('compatibilityTrait.elasticity'), isMatch: matches.elasticity },
  ]

  /* Portalled to <body>: the card lives inside .category-section's stacking
     context (isolate + z-surface), which would otherwise pin the overlay below
     the sticky products header and clip it against the card's overflow. */
  const modal = (
    <div
      id="compatibility-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="compatibility-modal-title"
      className="compatibility-modal-overlay"
      onClick={onClose}
    >
      <div className="compatibility-modal-backdrop" />
      <div ref={contentRef} className="compatibility-modal-content" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} aria-label={t('closeCompatibility')} className="compatibility-modal-close">
          ✕
        </button>

        <div className="compatibility-modal-header">
          <div className="compatibility-modal-avatar">
            <FaceImage id="compatibility-modal-face" score={score} size={88} alt="" priority />
          </div>
          <div className="compatibility-modal-header-text">
            <h2 id="compatibility-modal-title" className="compatibility-modal-title">
              {t('compatibilityModalTitlePrefix')} {productName} {t('compatibilityModalTitleSuffix')}
            </h2>
            <span className="compatibility-modal-badge" style={{ backgroundColor: badgeColor(score) }}>
              {t('compatibilityPercentModal', { percent })}
            </span>
          </div>
        </div>

        <ul className="compatibility-modal-list">
          {traits.map(({ key, label, isMatch }) => (
            <li key={key} className={`compatibility-modal-item ${isMatch ? 'is-match' : 'is-mismatch'}`}>
              <span className="compatibility-modal-item-icon" aria-hidden>{isMatch ? '✓' : '✕'}</span>
              <span>{label}</span>
            </li>
          ))}
        </ul>

        <p className="compatibility-modal-footer">{t('compatibilityModalFooter')}</p>
      </div>
    </div>
  )

  return createPortal(modal, document.body)
}
